import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { DetailPanelData } from '../../../Common/DetailPanel';
import { renderWeight } from '../../../../Utils/weight_utils';
import { Grey3 } from '../../../../color_utils';
import Routes from '../../../../routes';
import CreateEpaAlertDialog from '../../CreateEpaAlertDialog';
import { isDesktop } from '../../../../utils';

const MainDetails = ({
    data: {
        id = null,
        locationName = '',
        location = null,
        chargeCapacityLBS = '',
        assetSubTypeName = '',
        assetStatusName = '',
        leakRate = null,
        materialTypeName = '',
        calendarYearLeakRate = null,
        materialClass = null,
        aimActLeakRate = null,
        waStateLeakRate = null
    } = {},
    regulatedAssetTypes = [],
}) => {

    const initialFieldValue = {
        value: null,
        hasError: false,
    };

    const [openModal, setOpenModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleteLoading, setIsDeleteLoading] = useState(false)
    const [leakRatePercent, setLeakRatePercent] = useState(0)
    const [leakRateId, setLeakRateId] = useState(null)
    const [leakRateValue, setLeakRateValue] = useState(initialFieldValue);

    const handleOpen = () => setOpenModal(true)
    const handleClose = () => setOpenModal(false)

    const fetchLeakRates = async () => {
        const response = await fetch(Routes.Asset.GetLeakRateExceed(id));
        if (response.status === 200) {
            const data = await response.json();
            if (data) {
                setLeakRatePercent(data.userDefinedVar1);
                setLeakRateId(data.id);
            }
        } else {
            setLeakRatePercent(0);
            setLeakRateId(null);
        }
    }

    useEffect(() => {
        fetchLeakRates();
    }, [openModal])

    const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
    };

    const onModalClose = () => {
        handleClose();
        setLeakRateValue(initialFieldValue);
    };

    const handleOnChange = (value) => {
        setLeakRateValue({
            hasError: false,
            value: value,
        });
    };

    // leak rate from 5 - 100 %
    const leakRateOption = Array.from({ length: 96 }, (_, index) => ({
        id: index + 5,
        name: index + 5,
    }));

    const hasValue = leakRatePercent > 0 && leakRateId != null ? true : false;

    const onSave = async () => {
        try {
            setIsLoading(true);
            let isFormValid = true;
            if (!leakRateValue.value) {
                setLeakRateValue({ ...leakRateValue, hasError: true });
            }

            if (!isFormValid) {
                setIsLoading(false);
                return;
            }

            const body = {
                Id: leakRateId,
                assetId: id,
                LeakRatePercent: leakRateValue.value,
            };

            const endpoint = hasValue ? Routes.Asset.UpdateLeakRateExceed() : Routes.Asset.CreateLeakRateExceed();

            const response = await fetch(endpoint, {
                method: hasValue ? "PATCH" : "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (response.status === 200) {
                fetchLeakRates()
                setIsLoading(false);
                onModalClose();
            }
        } catch (e) {
            console.log(`fail save ${e}`);
            setIsLoading(false);
        }
    };

    const onDelete = async () => {
        try {
            setIsDeleteLoading(true);
            const response = await fetch(Routes.Asset.DeleteLeakRateExceed(leakRateId), {
                method: "PATCH",
                headers,
            });

            if (response.status === 200) {
                fetchLeakRates()
                onModalClose();
                setIsDeleteLoading(false);
            }
        } catch {
            setIsDeleteLoading(false);
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const headerTitle = hasValue ? "Update the percentage of the alert or delete the alert" : "Create an alert when leak rate exceeds:";
    const disabled = leakRateValue.value === null;

    const hasWaEcologyTag = regulatedAssetTypes.some(r => r.name === "WA Ecology" && r.isSelected);

    return (
        <>
            <DetailPanelData title="ID" data={id} />
            <DetailPanelData id='StatusHeader' title="STATUS" data={assetStatusName} />
            {assetSubTypeName !== '' && <DetailPanelData id='AssetSubType' title="SUBTYPE" data={assetSubTypeName} />}
            <DetailPanelData title="LOCATION" data={locationName} link={"/Location/"} linkId={location} />
            <DetailPanelData id='MaterialTypeHeader' title="MATERIAL TYPE" data={materialTypeName} />
            <DetailPanelData id='ChargeCapacityHeader' title="CHARGE CAPACITY" data={renderWeight(chargeCapacityLBS ? chargeCapacityLBS : 0)} />
            <DetailPanelData id='materialClass' title="MATERIAL CLASS" data={`${materialClass}`} />
            <DetailPanelData id='leakRate' title={"EPA METHOD LEAK RATE"} data={`${leakRate ? leakRate : 0}%`}
                createAlertButton={
                    isDesktop && (
                        <>
                            <FiberManualRecordIcon
                                style={{ fontSize: '5px', marginLeft: '5px', color: Grey3 }}
                                fontSize='small'
                            />
                            <Button
                                onClick={handleOpen}
                                className="subheading subheadingUnderLine"
                                style={{ color: 'grey', textTransform: leakRatePercent == 0 ? 'capitalize' : "none" }}
                                variant='text'
                            >
                                {hasValue ? `✓ Alert created: ${leakRatePercent}%` : "Create alert"}
                            </Button>
                        </>
                    )
                }
            />
            <DetailPanelData id='calendarYearLeakRate' title="CALENDAR YEAR LEAK RATE" data={`${calendarYearLeakRate.toFixed(1)} %`} />
            {regulatedAssetTypes.some(r => r.name === "EPA AIM Act" && r.isSelected) && (
                <DetailPanelData id='aimActLeakRate' title="EPA AIM ACT ROLLING METHOD LEAK RATE" data={`${aimActLeakRate != null ? aimActLeakRate : 0} %`} />
            )}
            {hasWaEcologyTag && (
                <DetailPanelData id='wa12MonthRollingAvg' title="WA 12 MONTH ROLLING AVG" data={`${waStateLeakRate != null ? waStateLeakRate.toFixed(1) : 0} %`} />
            )}

            <CreateEpaAlertDialog
                leakRatePercent={leakRatePercent}
                open={openModal}
                isDeleteLoading={isDeleteLoading}
                onSave={onSave}
                onDelete={onDelete}
                isLoading={isLoading}
                leakRate={leakRateValue}
                options={leakRateOption}
                handleOnChange={handleOnChange}
                headerName={headerTitle}
                buttonTitle={leakRatePercent > 0 ? "UPDATE ALERT" : "CREATE ALERT"}
                initialFieldValue={initialFieldValue}
                disabled={disabled}
                headerWidth={hasValue ? '80%' : '70%'}
                onModalClose={onModalClose} />
        </>
    )
}

export default MainDetails;